import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from 'firebase/messaging';
import { supabase } from './supabase';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// Variable pour suivre l'état de l'initialisation
let isInitializing = false;
let currentFcmToken: string | null = null;
let activeConversationId: string | null = null;

// Fonction pour définir la conversation active
export const setActiveConversation = (conversationId: string | null) => {
  activeConversationId = conversationId;
};

// Fonction pour afficher une notification
const showNotification = async (title: string, options: NotificationOptions) => {
  console.log('Tentative d\'affichage de notification avec les options:', { title, options });

  try {
    // Vérifier si on est dans la conversation active
    if (activeConversationId === options.data?.conversation_id && document.visibilityState === 'visible') {
      console.log('Dans la conversation active, notification ignorée');
      return;
    }

    if (!('Notification' in window)) {
      console.error('Ce navigateur ne supporte pas les notifications desktop');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.log('Permission des notifications non accordée');
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.error('Permission des notifications refusée');
        return;
      }
    }

    const notification = new Notification(title, options);
    notification.onclick = function() {
      console.log('Notification cliquée');
      window.focus();
      
      if (options.data?.conversation_id) {
        window.location.href = `/messages/${options.data.conversation_id}`;
      }
      
      notification.close();
    };
  } catch (error) {
    console.error('Erreur lors de l\'affichage de la notification:', error);
  }
};

const updateFcmToken = async (messaging: any, swRegistration: ServiceWorkerRegistration) => {
  try {
    // Récupérer l'utilisateur actuel d'abord
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    console.log('Vérification du token pour l\'utilisateur:', user.id);

    // Vérifier si nous avons déjà un token valide
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('fcm_token')
      .eq('user_id', user.id)
      .single();

    if (profileError) {
      console.error('Erreur lors de la récupération du profil:', profileError);
      throw profileError;
    }

    if (profile?.fcm_token) {
      try {
        // Vérifier si le token existant est toujours valide
        const existingToken = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: swRegistration
        });

        if (existingToken === profile.fcm_token) {
          console.log('Token FCM existant toujours valide');
          currentFcmToken = existingToken;
          return existingToken;
        }
      } catch (error) {
        console.log('Token existant invalide, génération d\'un nouveau token');
      }
    }

    // Si nous arrivons ici, nous devons générer un nouveau token
    console.log('Suppression de l\'ancien token...');
    await deleteToken(messaging);
    
    console.log('Génération d\'un nouveau token...');
    const newToken = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swRegistration
    });

    console.log('Nouveau token FCM généré, mise à jour dans la base de données...');

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ fcm_token: newToken })
      .eq('user_id', user.id);

    if (updateError) {
      console.error('Erreur lors de la mise à jour du token:', updateError);
      throw updateError;
    }

    console.log('Token FCM mis à jour avec succès pour l\'utilisateur:', user.id);
    currentFcmToken = newToken;
    return newToken;
  } catch (error) {
    console.error('Erreur lors du renouvellement du token FCM:', error);
    throw error;
  }
};

// Fonction pour détecter iOS
const isIOS = () => {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
  || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
};

// Fonction pour demander la permission des notifications
const requestNotificationPermission = async () => {
  console.log('Demande de permission pour les notifications...');
  
  try {
    if (isIOS()) {
      console.log('Plateforme iOS détectée');
      // Sur iOS, on doit d'abord vérifier si le navigateur supporte les notifications
      if (!('Notification' in window)) {
        console.error('Les notifications ne sont pas supportées sur ce navigateur iOS');
        return false;
      }
      
      // Sur iOS, on force une demande de permission même si déjà demandée
      const permission = await Notification.requestPermission();
      console.log('Permission iOS:', permission);
      return permission === 'granted';
    } else {
      // Pour les autres plateformes
      if (Notification.permission === 'granted') {
        console.log('Permission déjà accordée');
        return true;
      }
      
      const permission = await Notification.requestPermission();
      console.log('Permission obtenue:', permission);
      return permission === 'granted';
    }
  } catch (error) {
    console.error('Erreur lors de la demande de permission:', error);
    return false;
  }
};

export const initializeFirebaseMessaging = async () => {
  // Éviter les initialisations multiples
  if (isInitializing) {
    console.log('Initialisation déjà en cours...');
    return;
  }

  if (currentFcmToken) {
    console.log('Firebase Messaging déjà initialisé');
    return;
  }

  isInitializing = true;

  try {
    console.log('Initialisation de Firebase Messaging...');
    
    const isMessagingSupported = await isSupported();
    if (!isMessagingSupported) {
      console.log('Firebase Messaging n\'est pas supporté sur ce navigateur');
      return;
    }

    // Demander la permission avant d'enregistrer le Service Worker
    const permissionGranted = await requestNotificationPermission();
    if (!permissionGranted) {
      console.log('Permission des notifications non accordée');
      return;
    }

    let swRegistration: ServiceWorkerRegistration | undefined;
    if ('serviceWorker' in navigator) {
      try {
        console.log('Enregistrement du Service Worker...');
        swRegistration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('Service Worker enregistré avec succès');
        
        await navigator.serviceWorker.ready;
        console.log('Service Worker est prêt');
      } catch (error) {
        console.error('Erreur lors de l\'enregistrement du Service Worker:', error);
        return;
      }
    }

    const messaging = getMessaging(app);

    try {
      await updateFcmToken(messaging, swRegistration!);
    } catch (error) {
      console.error('Erreur lors de la gestion du token FCM:', error);
      return;
    }

    // Gérer les messages en premier plan
    onMessage(messaging, async (payload) => {
      console.log('Message reçu en premier plan:', payload);
      
      // Vérifier si le message est destiné à cet utilisateur
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('fcm_token')
        .eq('user_id', user.id)
        .single();

      // Vérifier si l'utilisateur est dans la conversation
      const conversationId = payload.data?.conversation_id;
      const currentPath = window.location.pathname;
      const isInConversation = currentPath === `/messages/${conversationId}` && 
                              document.visibilityState === 'visible';

      // Ne pas afficher la notification si l'utilisateur est dans la conversation
      if (isInConversation) {
        console.log('Utilisateur dans la conversation, notification ignorée');
        return;
      }

      // Ne traiter la notification que si le token correspond
      if (profile?.fcm_token === payload.data?.recipientToken) {
        // Afficher la notification
        showNotification(payload.notification?.title || 'Nouveau message', {
          body: payload.notification?.body,
          icon: '/icon-192x192.png',
          badge: '/icon-192x192.png',
          data: payload.data,
          tag: conversationId,
          requireInteraction: true
        });
      } else {
        console.log('Message ignoré - token ne correspond pas');
      }
    });

    console.log('Initialisation de Firebase Messaging terminée avec succès');
  } catch (error) {
    console.error('Erreur lors de l\'initialisation des notifications:', error);
  } finally {
    isInitializing = false;
  }
}; 